import React from 'react';
import { useFetcher } from 'react-router-dom';

import type { VisibilityConfigInput } from '~/api/graphql';

/**
 * Bulk-visibility plumbing for library list pages. Mirrors
 * `useLibraryBulkArchive`: the caller hands us the route action path (e.g.
 * `/app/library/styles/visibility`) and we submit through a router fetcher
 * so the parent loader revalidates without a navigation.
 *
 * The action is expected to return `{ failedIds: string[] }` so this hook
 * can keep the failing rows selected and surface a partial-failure message.
 */
export function useLibraryBulkVisibility(params: {
  /** Absolute action path, e.g. `/app/library/forms/visibility`. */
  actionPath: string;
  /** JSON key under which to submit the IDs (defaults to `templateIds`). */
  payloadKey?: string;
  /** Plural noun used in the "Failed {label} remain selected" message. */
  itemPlural: string;
  /** Called once a fully-successful save resolves so the caller can clear selection. */
  onUpdated?: () => void;
}) {
  const fetcher = useFetcher<{ failedIds?: string[] }>();
  const { actionPath, payloadKey = 'templateIds', itemPlural, onUpdated } = params;

  const [open, setOpen] = React.useState(false);
  const [ids, setIds] = React.useState<string[]>([]);
  const [resultMessage, setResultMessage] = React.useState<React.ReactNode>(null);

  // True between `save()` and the next idle that follows it; lets the effect
  // below distinguish a real action result from `fetcher.data` left over
  // from a previous submission.
  const pendingRef = React.useRef(false);
  // Snapshot of the ids submitted to the action; failedIds.length compared
  // against this gives the "X updated, Y failed" counts.
  const submittedIdsRef = React.useRef<string[]>([]);

  const isUpdating =
    fetcher.state === 'submitting' || (fetcher.state === 'loading' && pendingRef.current);

  React.useEffect(() => {
    if (fetcher.state !== 'idle' || !pendingRef.current) return;
    pendingRef.current = false;

    const failedIds = fetcher.data?.failedIds ?? [];
    const submitted = submittedIdsRef.current;
    submittedIdsRef.current = [];

    if (failedIds.length === 0) {
      setOpen(false);
      setIds([]);
      setResultMessage(null);
      onUpdated?.();
      return;
    }

    const updatedCount = submitted.length - failedIds.length;
    setIds(failedIds);
    setResultMessage(
      `${updatedCount} updated, ${failedIds.length} failed. Failed ${itemPlural} remain selected.`
    );
  }, [fetcher.data, fetcher.state, itemPlural, onUpdated]);

  const openDialog = React.useCallback((selectedIds: string[]) => {
    setIds(selectedIds);
    setResultMessage(null);
    setOpen(true);
  }, []);

  const closeDialog = React.useCallback(() => {
    setOpen(false);
    setResultMessage(null);
  }, []);

  const save = React.useCallback(
    (visibility: VisibilityConfigInput) => {
      if (ids.length === 0) return;
      pendingRef.current = true;
      submittedIdsRef.current = ids;
      fetcher.submit(
        { [payloadKey]: ids, visibility },
        { method: 'POST', action: actionPath, encType: 'application/json' }
      );
    },
    [actionPath, fetcher, ids, payloadKey]
  );

  return {
    open,
    ids,
    isUpdating,
    resultMessage,
    openDialog,
    closeDialog,
    save
  };
}
